import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Shield, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OwnerLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const trimmedSecret = secret.trim();
    if (!trimmedSecret) {
      setError('Please enter the secret code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/owner-login`, {
        secret: trimmedSecret
      });
      
      if (response.data && response.data.access_token) {
        login(response.data.access_token, response.data.user);
        toast.success('Welcome back, Owner!');
        navigate('/admin');
        return;
      }
      setError('Invalid owner secret code');
    } catch (err) {
      console.error('[OwnerLogin] Login error:', err);
      setError(err.response?.data?.detail || 'Invalid secret code or login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl"></div>
      </div>

      <Card className="w-full max-w-md glass-card border-red-900/50 relative z-10">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <CardTitle className="text-2xl text-red-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Owner Access
          </CardTitle>
          <p className="text-sm text-gray-500 mt-2">
            Restricted area. Authorized personnel only.
          </p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Secret Code</label>
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="Enter secret code..."
                  className="bg-black/50 border-red-900/30 pr-10"
                  data-testid="owner-secret-input"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700"
              disabled={loading}
              data-testid="owner-login-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Authenticating...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Access Panel
                </div>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-red-900/20 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              ← Return to Home
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OwnerLogin;
